import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, LogOut, Save, X, ExternalLink, Inbox, Briefcase, Image as ImageIcon, MessageSquare, Settings as SettingsIcon, Upload } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { useSettings } from "../lib/SettingsContext";

const TABS = [
  { id: "inquiries", label: "Inquiries", icon: Inbox },
  { id: "services", label: "Services", icon: Briefcase },
  { id: "portfolio", label: "Portfolio", icon: ImageIcon },
  { id: "testimonials", label: "Testimonials", icon: MessageSquare },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function Admin() {
  const nav = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [me, setMe] = useState(null);
  const { settings } = useSettings();
  const logoSrc = resolveImageUrl(settings?.logo_url) || "/logo.png";

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {
      localStorage.removeItem("pp_token");
      nav("/admin/login");
    });
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("pp_token");
    localStorage.removeItem("pp_user");
    nav("/admin/login");
  };

  if (!me) return <div className="min-h-screen grid place-items-center text-gray-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-brand-mist" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-200">
        <div className="container-x flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={logoSrc} alt="PPrints" className="h-10 w-10 rounded-full object-cover bg-white ring-1 ring-gray-200" />
            <span className="font-display font-extrabold text-lg">
              <span className="text-brand-ink">P</span><span className="text-brand-green">P</span><span className="text-brand-ink">rints</span>
              <span className="text-gray-500 text-sm font-semibold ml-1">Admin</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-gray-600 hover:text-brand-blue inline-flex items-center gap-1">
              <ExternalLink className="h-4 w-4" /> View site
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-brand-blue">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-8 grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <nav className="bg-white border border-gray-200 rounded-2xl p-2 sticky top-6">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition ${
                  tab === t.id ? "bg-brand-blue text-white" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="lg:col-span-9">
          {tab === "inquiries" && <InquiriesTab />}
          {tab === "services" && <CrudTab key="services" resource="services" label="Service" fields={SERVICE_FIELDS} />}
          {tab === "portfolio" && <CrudTab key="portfolio" resource="portfolio" label="Portfolio Item" fields={PORTFOLIO_FIELDS} />}
          {tab === "testimonials" && <CrudTab key="testimonials" resource="testimonials" label="Testimonial" fields={TESTIMONIAL_FIELDS} />}
          {tab === "settings" && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

function InquiriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/inquiries").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/inquiries/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Inquiries</h1>
          <p className="text-sm text-gray-500 mt-1">{items.length} total leads</p>
        </div>
      </div>
      {loading ? <p className="text-gray-500">Loading...</p> : (
        items.length === 0 ? <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center text-gray-500">No inquiries yet.</div> : (
          <div className="space-y-3">
            {items.map((i) => (
              <div key={i.id} data-testid={`inquiry-${i.id}`} className="bg-white border border-gray-200 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-bold text-lg">{i.name}</div>
                    <div className="text-sm text-gray-500">{i.email} · {i.phone}{i.service ? ` · ${i.service}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{new Date(i.created_at).toLocaleString()}</span>
                    <button onClick={() => remove(i.id)} className="h-8 w-8 grid place-items-center rounded-lg text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-gray-700 leading-relaxed text-sm">{i.message}</p>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

const SERVICE_FIELDS = [
  { name: "title", label: "Title", type: "text" },
  { name: "description", label: "Description", type: "textarea" },
  { name: "icon", label: "Lucide Icon (e.g. Printer, Layers, Megaphone)", type: "text" },
  { name: "image_url", label: "Image (optional — shows above icon if uploaded)", type: "image" },
  { name: "order", label: "Order", type: "number" },
];
const PORTFOLIO_FIELDS = [
  { name: "title", label: "Title", type: "text" },
  { name: "category", label: "Category", type: "text" },
  { name: "image_url", label: "Image", type: "image" },
  { name: "order", label: "Order", type: "number" },
];
const TESTIMONIAL_FIELDS = [
  { name: "name", label: "Customer Name", type: "text" },
  { name: "role", label: "Role / Company", type: "text" },
  { name: "quote", label: "Quote", type: "textarea" },
  { name: "rating", label: "Rating (1-5)", type: "number" },
  { name: "avatar_url", label: "Customer Photo / Logo (optional)", type: "image" },
  { name: "order", label: "Order", type: "number" },
];

function emptyFromFields(fields) {
  return Object.fromEntries(fields.map((f) => [f.name, f.type === "number" ? 0 : ""]));
}

// Resolve relative /api/uploads/... URLs to absolute for img src
export function resolveImageUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
}

function ImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5 MB. Please compress and try again.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-1 space-y-3">
      {value && (
        <div className="relative inline-block">
          <img src={resolveImageUrl(value)} alt="preview" className="h-32 w-32 rounded-xl object-cover border border-gray-200" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 h-7 w-7 grid place-items-center rounded-full bg-red-600 text-white shadow"
            title="Remove"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <label className={`inline-flex items-center gap-2 rounded-xl border-2 border-dashed border-gray-300 px-4 py-3 cursor-pointer hover:border-brand-blue hover:bg-brand-blue/5 transition ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          <Upload className="h-4 w-4 text-brand-blue" />
          <span className="text-sm font-semibold text-brand-blue">{uploading ? "Uploading..." : "Upload from device"}</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            disabled={uploading}
          />
        </label>
        <span className="text-xs text-gray-400">or paste a URL ↓</span>
      </div>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://... or /api/uploads/..."
        className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none text-sm"
      />
      <p className="text-xs text-gray-500">Tip: JPG / PNG / WebP / GIF up to 5 MB. For best results, compress to under 1 MB.</p>
    </div>
  );
}

function CrudTab({ resource, label, fields }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyFromFields(fields));

  const load = () => api.get(`/${resource}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [resource]);

  const startNew = () => { setForm(emptyFromFields(fields)); setEditing("new"); };
  const startEdit = (item) => {
    const f = {}; fields.forEach((fl) => { f[fl.name] = item[fl.name] ?? (fl.type === "number" ? 0 : ""); });
    setForm(f); setEditing(item.id);
  };
  const cancel = () => { setEditing(null); };

  const save = async () => {
    const payload = { ...form };
    fields.forEach((f) => { if (f.type === "number") payload[f.name] = Number(payload[f.name]) || 0; });
    try {
      if (editing === "new") await api.post(`/${resource}`, payload);
      else await api.put(`/${resource}/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}?`)) return;
    await api.delete(`/${resource}/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl font-extrabold">{label}s</h1>
          <p className="text-sm text-gray-500 mt-1">{items.length} items</p>
        </div>
        <button onClick={startNew} data-testid={`${resource}-new`} className="btn-primary text-sm py-2.5">
          <Plus className="h-4 w-4" /> New {label}
        </button>
      </div>

      {editing && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">{editing === "new" ? `New ${label}` : `Edit ${label}`}</h3>
            <button onClick={cancel} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-gray-100"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.name} className={f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
                <label className="overline text-gray-500">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea rows={3} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none resize-none" />
                ) : f.type === "image" ? (
                  <ImageField value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} />
                ) : (
                  <input type={f.type} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none" />
                )}
              </div>
            ))}
          </div>
          <div className="mt-5 flex gap-2">
            <button onClick={save} data-testid={`${resource}-save`} className="btn-primary text-sm py-2.5"><Save className="h-4 w-4" /> Save</button>
            <button onClick={cancel} className="btn-outline text-sm py-2.5">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="bg-white border border-gray-200 rounded-2xl p-5 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              {(item.image_url || item.avatar_url) && (
                <img src={resolveImageUrl(item.image_url || item.avatar_url)} alt="" className="h-14 w-14 rounded-lg object-cover border border-gray-200 shrink-0" />
              )}
              <div className="min-w-0">
                <div className="font-display font-bold">{item.title || item.name}</div>
                <div className="text-sm text-gray-500 truncate">{item.description || item.quote || item.category}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => startEdit(item)} className="h-9 w-9 grid place-items-center rounded-lg text-brand-blue hover:bg-brand-blue/10"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(item.id)} className="h-9 w-9 grid place-items-center rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);
  const { refresh } = useSettings();

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/settings", s);
      await refresh();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  if (!s) return <p className="text-gray-500">Loading...</p>;

  const textFields = [
    ["phone", "Phone"],
    ["whatsapp", "WhatsApp Number (digits only, e.g. 919999999999)"],
    ["email", "Email"],
    ["website", "Website"],
    ["address", "Address"],
    ["business_hours", "Business Hours"],
    ["map_embed_url", "Google Maps Embed URL"],
    ["tagline", "Hero Tagline"],
    ["showcase_video_title", "Showcase Video — Heading (shown above video)"],
    ["showcase_video_url", "Showcase Video — YouTube / Vimeo / MP4 URL (leave blank to hide section)"],
  ];

  const imageFields = [
    ["logo_url", "Site Logo (shown in navbar, footer, admin)"],
    ["hero_image_url", "Hero Section Image"],
    ["about_image_1_url", "About — Image 1 (top-left)"],
    ["about_image_2_url", "About — Image 2 (top-right)"],
    ["about_image_3_url", "About — Image 3 (bottom-left)"],
  ];

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold">Site Settings</h1>
      <p className="text-sm text-gray-500 mt-1">Edit contact info, branding, and homepage images.</p>

      <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Contact &amp; Info</h2>
        {textFields.map(([k, l]) => (
          <div key={k}>
            <label className="overline text-gray-500">{l}</label>
            {k === "address" || k === "map_embed_url" ? (
              <textarea rows={2} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none resize-none" />
            ) : (
              <input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none" />
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
        <h2 className="font-display font-bold text-lg">Branding &amp; Images</h2>
        <p className="text-sm text-gray-500">Upload your real logo, hero image, and about-section photos. Leave blank to use defaults.</p>
        {imageFields.map(([k, l]) => (
          <div key={k}>
            <label className="overline text-gray-500">{l}</label>
            <ImageField value={s[k] || ""} onChange={(v) => setS({ ...s, [k]: v })} />
          </div>
        ))}
      </div>

      <div className="mt-6">
        <button onClick={save} disabled={loading} data-testid="settings-save" className="btn-primary disabled:opacity-60">
          <Save className="h-4 w-4" /> {loading ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
